/**
 * Cálculo de pricing para contratos recorrentes.
 *
 * Função pura compartilhada entre backend e frontend.
 *  - Backend usa em criar-proposta/atualizar-proposta pra gerar snapshot.
 *  - Frontend usa em /propostas/nova pra painel reativo.
 *  - Frontend público lê o snapshot pronto da proposta (sem recalcular).
 *
 * Total mensal em 3 blocos: Pacote + Inventário + Add-ons.
 * Onboarding parcelado em 6× é devolvido separado.
 */

import type {
  DescontoEscalonado,
  PropostaHaasItem,
  PropostaPremiumModulo,
} from './types/payloads.js';
import type { Bundle, Multiplicadores, TabelaPrecosCompleta } from './types/tabela-precos.js';

export type PacoteKey = 'essencial' | 'professional' | 'premium';
export type PerfilKey = 'simples' | 'moderado' | 'critico';
export type OnboardingKey = 'none' | 'express' | 'standard' | 'enterprise';

export interface RecursosPricing {
  dispositivos: number;
  usuarios: number;
  servidores: number;
  horasSuporte: number;
}

export interface InventarioPricing {
  impressoras: number;
  nobreaks: number;
  firewalls: number;
  roteadoresAp: number;
  switches: number;
  dvrs: number;
  storages: number;
  outros: number;
}

export const INVENTARIO_PRICING_VAZIO: InventarioPricing = {
  impressoras: 0,
  nobreaks: 0,
  firewalls: 0,
  roteadoresAp: 0,
  switches: 0,
  dvrs: 0,
  storages: 0,
  outros: 0,
};

/**
 * Sugestão de horas de suporte mensais para o pacote Essencial.
 *
 * Regra de negócio:
 *  - Base: 30% da quantidade de estações
 *  - Perfil simples:  ×1.00  (30%)
 *  - Perfil moderado: ×1.10  (33%)
 *  - Perfil crítico:  ×1.20  (36%)
 *  - Arredonda sempre pra cima (horas cheias)
 *  - Garante pelo menos +1h entre perfis (moderado > simples, critico > moderado)
 *
 * Esse valor é apenas sugestão pra pré-preencher o form — vendedor pode editar.
 */
export function sugerirHorasSuporteEssencial(estacoes: number, perfil: PerfilKey): number {
  if (estacoes <= 0) return 0;
  const base = estacoes * 0.3;
  const simples = Math.ceil(base);
  let moderado = Math.ceil(base * 1.1);
  if (moderado <= simples) moderado = simples + 1;
  let critico = Math.ceil(base * 1.2);
  if (critico <= moderado) critico = moderado + 1;
  if (perfil === 'critico') return critico;
  if (perfil === 'moderado') return moderado;
  return simples;
}

export interface AddOnsPricing {
  backupM365: number;
  backupEstacao500gb: number;
  backupServidorFisico2tb: number;
  backupServidorVirtual2tb: number;
  /** GB adicionais cobrados por unidade — backupNuvem.perGb */
  backupPerGb: number;
  /** Quantidade de testes de restore mensais — backupNuvem.testeRestore */
  backupTesteRestore: number;
  /** DRaaS (Disaster Recovery as a Service) — por backup de servidor — backupNuvem.draas */
  draas: number;
  avGerenciado: number;
  avEdr: number;
  mikrotik5p: number;
  mikrotikWireless: number;
  gestaoFirewallCliente: number;
}

export const ADDONS_PRICING_VAZIO: AddOnsPricing = {
  backupM365: 0,
  backupEstacao500gb: 0,
  backupServidorFisico2tb: 0,
  backupServidorVirtual2tb: 0,
  backupPerGb: 0,
  backupTesteRestore: 0,
  draas: 0,
  avGerenciado: 0,
  avEdr: 0,
  mikrotik5p: 0,
  mikrotikWireless: 0,
  gestaoFirewallCliente: 0,
};

/**
 * Onboarding aceita 3 formatos:
 *  - string ('none'|'express'|...): tier legado (propostas antigas).
 *  - { modo: 'nenhum' }: novo modelo sem cobrar.
 *  - { modo: 'sugerido'|'manual', valorFinal, ... }: novo dimensional.
 */
export type OnboardingInput =
  | OnboardingKey
  | { modo: 'nenhum' }
  | {
      modo: 'sugerido' | 'manual';
      valorFinal: number;
      valorSugerido: number;
      breakdown: {
        baseFixo: number;
        linhas: Array<{ label: string; qtd: number; unitario: number; subtotal: number }>;
        subtotalAntesMultiplicador: number;
        perfil: PerfilKey;
        multiplicador: number;
        total: number;
      };
    };

export interface InputCalculoPricing {
  tabela: TabelaPrecosCompleta;
  pacote: PacoteKey;
  perfil: PerfilKey;
  onboarding: OnboardingInput;
  recursos: RecursosPricing;
  inventario: InventarioPricing;
  addons: AddOnsPricing;
  /** Itens HaaS contratados — opcional. Vazio/undefined = sem HaaS (não soma ao recorrente, não dispara Adendo). */
  haasItens?: PropostaHaasItem[];
  /** Módulos Premium contratados (snapshot). Combo só dispara se TODOS marcados. */
  premiumModulos?: PropostaPremiumModulo[];
  /** Desconto promocional escalonado — opcional. Se presente, gera cronograma. */
  descontoEscalonado?: {
    faixas: Array<{
      tipo: 'pct' | 'valor';
      valor: number;
      duracaoMeses: number;
      motivo?: string;
    }>;
    aplicarEm: 'mensal' | 'mensal-onboarding';
  };
  /** Prazo de fidelidade do contrato (extrai duração em meses). Default 12. */
  prazoFidelidadeMeses?: number;
  /**
   * Proposta AVULSA (à la carte): vende só add-ons, sem pacote de gestão base.
   * Quando true, o mensal ignora pacote base, inventário e módulos Premium —
   * soma apenas add-ons (+ HaaS). `pacote` fica como placeholder e não cobra.
   */
  semPacoteBase?: boolean;
}

/**
 * Breakdown HaaS — uma linha por item HaaS contratado, com subtotal qtd × valor.
 * Total mensal entra no recorrente do contrato.
 * Total venda é usado pra cálculo de residual (cláusula 7) e parcela não amortizada (cláusula 9.2).
 */
export interface HaasBreakdownLinha {
  tipo: string;
  modelo: string;
  qtd: number;
  valorMensalUnit: number;
  precoVendaUnit: number;
  pctHardware: number;
  subtotalMensal: number;
  subtotalVenda: number;
}

export interface HaasBreakdown {
  linhas: HaasBreakdownLinha[];
  totalMensal: number;
  totalVenda: number;
}

/**
 * Breakdown Módulos Premium — uma linha por módulo contratado.
 * Combo desconto aplicado quando TODOS os módulos ATIVOS do catálogo estão marcados.
 */
export interface PremiumModulosBreakdownLinha {
  id: string;
  nome: string;
  escopoResumido: string;
  /** Valor/hora congelado no snapshot da proposta. */
  valorHora: number;
  /** Horas calculadas pela regra de estações. */
  horas: number;
  /** = valorHora × horas */
  valorMensal: number;
}

export interface PremiumModulosBreakdown {
  linhas: PremiumModulosBreakdownLinha[];
  somaItens: number;
  todosMarcados: boolean;
  comboPct: number;
  comboDesconto: number;
  totalLiquido: number;
}

export interface LinhaBreakdown {
  label: string;
  qtd: number;
  unitario: number;
  subtotal: number;
  multiplicadorAplicado: boolean;
}

export interface BreakdownPricing {
  pacote: { nome: string; linhas: LinhaBreakdown[]; subtotal: number };
  inventario: { linhas: LinhaBreakdown[]; subtotal: number };
  addons: { linhas: LinhaBreakdown[]; subtotal: number };
  /**
   * Onboarding no breakdown:
   *  - `tipo` segue legado pra retrocompat (tiers ou 'none' ou 'dimensional').
   *  - `total` e `parcela` (6×) válidos pros 2 modelos.
   *  - `breakdown` só populado quando vem do modelo dimensional novo.
   *  - `editadoManual` true se modo='manual' (valor final ≠ sugerido).
   */
  onboarding: {
    tipo: OnboardingKey | 'dimensional';
    total: number;
    parcela: number;
    valorSugerido?: number;
    editadoManual?: boolean;
    breakdown?: {
      baseFixo: number;
      linhas: Array<{ label: string; qtd: number; unitario: number; subtotal: number }>;
      subtotalAntesMultiplicador: number;
      multiplicador: number;
    };
  };
  multiplicador: number;
  mensalRecorrente: number;
  mensalPrimeiros6Meses: number;
  totalContrato12m: number;
  perfil: PerfilKey;
  pacote_key: PacoteKey;
  /** Proposta avulsa (à la carte, sem pacote base) — views escondem comparativo/pacote. */
  semPacoteBase?: boolean;
  /** HaaS — só presente se proposta tem itens HaaS contratados. Soma já refletida em mensalRecorrente. */
  haas?: HaasBreakdown;
  /** Módulos Premium — só presente se proposta tem módulos. Soma líquida já refletida em mensalRecorrente. */
  premiumModulos?: PremiumModulosBreakdown;
  /** Desconto promocional escalonado — cronograma calculado e total economizado. */
  descontoEscalonado?: DescontoEscalonadoBreakdown;
}

/**
 * Linha do cronograma de desconto — uma faixa contígua de meses com mesmo valor.
 * A última linha (sem desconto) representa os meses restantes do contrato.
 */
export interface CronogramaLinha {
  /** Faixa: 'mês 1-3' */
  mesInicio: number;
  mesFim: number;
  duracaoMeses: number;
  valorMensal: number;
  /** Tipo da faixa, ou null pra faixa final 'valor cheio' */
  tipo: 'pct' | 'valor' | null;
  /** Valor configurado na faixa (pct ou R$). null pra valor cheio. */
  valorAplicado: number | null;
  motivo?: string;
}

export interface DescontoEscalonadoBreakdown {
  /** Linhas do cronograma: cada faixa + a final 'valor cheio' se houver meses restantes */
  cronograma: CronogramaLinha[];
  /** Mensal recorrente sem desconto (referência) */
  mensalCheio: number;
  /** Total dos primeiros N meses (toda a vigência do contrato) com desconto aplicado */
  totalContratoComDesconto: number;
  /** Total se nada tivesse sido descontado (referência) */
  totalContratoSemDesconto: number;
  /** Economia total = sem - com */
  economiaTotal: number;
  /** Configuração original que originou esse cronograma */
  config: {
    aplicarEm: 'mensal' | 'mensal-onboarding';
    duracaoContratoMeses: number;
  };
}

/**
 * Calcula o cronograma escalonado de desconto sobre o mensal recorrente.
 *
 * Regra: faixas aplicadas em ordem (faixa 1 = primeiros N meses). Após a soma
 * das durações das faixas, o restante do contrato volta ao valor cheio.
 *
 * Se `desconto.aplicarEm === 'mensal-onboarding'`, a parcela mensal do
 * onboarding (já agregada em `onboardingParcela`) entra na base do desconto.
 * Caso contrário, o desconto incide apenas sobre `mensalRecorrente`.
 *
 * @param mensalRecorrente Soma fixa cobrada todo mês (pacote+inv+addons+haas+modulos)
 * @param onboardingParcela Parcela mensal do setup (entra na base só se aplicarEm=mensal-onboarding)
 * @param duracaoContratoMeses Quantos meses dura a vigência (extraído de prazoFidelidade)
 * @param desc Configuração de desconto escalonado
 */
export function calcularDescontoEscalonado(
  mensalRecorrente: number,
  onboardingParcela: number,
  duracaoContratoMeses: number,
  desc: {
    faixas: Array<{
      tipo: 'pct' | 'valor';
      valor: number;
      duracaoMeses: number;
      motivo?: string;
    }>;
    aplicarEm: 'mensal' | 'mensal-onboarding';
  },
): DescontoEscalonadoBreakdown {
  // Modelo mes-a-mes. Onboarding so existe nos 6 primeiros meses (parcela 1/6).
  // Quando aplicarEm='mensal-onboarding', o desconto incide sobre (mensal + parcelaOnb)
  // SOMENTE enquanto a parcela existe; depois incide so sobre mensal.
  // Quando aplicarEm='mensal', desconto incide so no mensal — parcelaOnb fica
  // somada por fora (sem desconto) nos meses 1-6.
  const NUM_PARCELAS_ONB = 6;

  // Pré-calcula em qual faixa cada mês cai (1-indexed).
  const faixaPorMes: Array<{
    faixa: (typeof desc.faixas)[number] | null;
  }> = [];
  let mesAtual = 1;
  for (const f of desc.faixas) {
    for (let i = 0; i < f.duracaoMeses && mesAtual <= duracaoContratoMeses; i++) {
      faixaPorMes.push({ faixa: f });
      mesAtual++;
    }
    if (mesAtual > duracaoContratoMeses) break;
  }
  // Resto vai cheio.
  while (mesAtual <= duracaoContratoMeses) {
    faixaPorMes.push({ faixa: null });
    mesAtual++;
  }

  // Helper: calcula valor cobrado num determinado mês (1-indexed).
  function valorDoMes(mes: number): number {
    const onbAtivo = mes <= NUM_PARCELAS_ONB && onboardingParcela > 0;
    const parcelaOnb = onbAtivo ? onboardingParcela : 0;
    const baseDesconto =
      desc.aplicarEm === 'mensal-onboarding' ? mensalRecorrente + parcelaOnb : mensalRecorrente;
    const faixa = faixaPorMes[mes - 1]?.faixa;
    let cobrado: number;
    if (!faixa) {
      cobrado = baseDesconto;
    } else if (faixa.tipo === 'pct') {
      cobrado = baseDesconto * (1 - faixa.valor / 100);
    } else {
      cobrado = baseDesconto - faixa.valor;
    }
    if (cobrado < 0) cobrado = 0;
    // Quando aplicarEm='mensal', parcelaOnb fica POR FORA (sem desconto):
    if (desc.aplicarEm === 'mensal') cobrado += parcelaOnb;
    return cobrado;
  }

  // Calcula valor de cada mês e total.
  const valoresPorMes: number[] = [];
  let totalComDesconto = 0;
  for (let m = 1; m <= duracaoContratoMeses; m++) {
    const v = valorDoMes(m);
    valoresPorMes.push(v);
    totalComDesconto += v;
  }

  // Agrupa meses contíguos com mesmo valor numa linha do cronograma.
  // Quebra também quando muda a faixa (pra preservar a tag de desconto exibida).
  const cronograma: CronogramaLinha[] = [];
  let mesIni = 1;
  while (mesIni <= duracaoContratoMeses) {
    const valor = valoresPorMes[mesIni - 1] ?? 0;
    const faixaIni = faixaPorMes[mesIni - 1]?.faixa ?? null;
    let mesFim = mesIni;
    while (
      mesFim + 1 <= duracaoContratoMeses &&
      (valoresPorMes[mesFim] ?? 0) === valor &&
      (faixaPorMes[mesFim]?.faixa ?? null) === faixaIni
    ) {
      mesFim++;
    }
    cronograma.push({
      mesInicio: mesIni,
      mesFim,
      duracaoMeses: mesFim - mesIni + 1,
      valorMensal: valor,
      tipo: faixaIni ? faixaIni.tipo : null,
      valorAplicado: faixaIni ? faixaIni.valor : null,
      ...(faixaIni?.motivo ? { motivo: faixaIni.motivo } : {}),
    });
    mesIni = mesFim + 1;
  }

  // Total sem desconto: soma reta (mensal + parcelaOnb nos 6 primeiros meses).
  let totalSemDesconto = 0;
  for (let m = 1; m <= duracaoContratoMeses; m++) {
    const onbAtivo = m <= NUM_PARCELAS_ONB && onboardingParcela > 0;
    totalSemDesconto += mensalRecorrente + (onbAtivo ? onboardingParcela : 0);
  }

  return {
    cronograma,
    mensalCheio: mensalRecorrente,
    totalContratoComDesconto: totalComDesconto,
    totalContratoSemDesconto: totalSemDesconto,
    economiaTotal: Math.max(0, totalSemDesconto - totalComDesconto),
    config: {
      aplicarEm: desc.aplicarEm,
      duracaoContratoMeses,
    },
  };
}

/**
 * Calcula subtotal HaaS — soma qtd × valor unitário de cada item.
 * Função pura; aceita undefined/[] e retorna totais zerados.
 */
export function calcularSubtotalHaas(itens: PropostaHaasItem[] | undefined): HaasBreakdown {
  if (!itens || itens.length === 0) {
    return { linhas: [], totalMensal: 0, totalVenda: 0 };
  }
  const linhas: HaasBreakdownLinha[] = itens.map((it) => ({
    tipo: it.tipo,
    modelo: it.modelo,
    qtd: it.quantidade,
    valorMensalUnit: it.valorMensalUnit,
    precoVendaUnit: it.precoVendaUnit,
    pctHardware: it.pctHardware,
    subtotalMensal: it.quantidade * it.valorMensalUnit,
    subtotalVenda: it.quantidade * it.precoVendaUnit,
  }));
  const totalMensal = linhas.reduce((s, l) => s + l.subtotalMensal, 0);
  const totalVenda = linhas.reduce((s, l) => s + l.subtotalVenda, 0);
  return { linhas, totalMensal, totalVenda };
}

/**
 * Snapshot do pricing gravado na proposta.
 * Imutável após criação (a menos que a proposta seja editada explicitamente).
 *
 * `tabelaCongelada` mantém os preços vigentes no momento da geração — assim
 * propostas antigas não mudam de valor se admin editar a tabela depois.
 */

/**
 * Calcula subtotal Módulos Premium + combo desconto.
 *
 * Combo dispara quando TODOS os módulos ATIVOS do catálogo estão na proposta.
 * Função pura; aceita undefined/[] e retorna totais zerados.
 */
export function calcularSubtotalPremiumModulos(
  selecionados: PropostaPremiumModulo[] | undefined,
  tabela: TabelaPrecosCompleta,
): PremiumModulosBreakdown {
  const comboPctDefault = tabela.venda.premiumModulos?.comboPct ?? 0;
  if (!selecionados || selecionados.length === 0) {
    return {
      linhas: [],
      somaItens: 0,
      todosMarcados: false,
      comboPct: comboPctDefault,
      comboDesconto: 0,
      totalLiquido: 0,
    };
  }
  const linhas: PremiumModulosBreakdownLinha[] = selecionados.map((s) => ({
    id: s.id,
    nome: s.nome,
    escopoResumido: s.escopoResumido,
    valorHora: s.valorHora,
    horas: s.horas,
    valorMensal: s.valorMensal,
  }));
  const somaItens = linhas.reduce((a, l) => a + l.valorMensal, 0);
  const catAtivos = tabela.venda.premiumModulos?.itens?.filter((m) => m.ativo) ?? [];
  const todosMarcados =
    catAtivos.length > 0 && catAtivos.every((cat) => selecionados.some((s) => s.id === cat.id));
  const comboDesconto = todosMarcados ? (somaItens * comboPctDefault) / 100 : 0;
  return {
    linhas,
    somaItens,
    todosMarcados,
    comboPct: comboPctDefault,
    comboDesconto,
    totalLiquido: somaItens - comboDesconto,
  };
}

export interface PricingSnapshot {
  versaoTabela: number;
  geradoEm: string; // ISO timestamp
  tabelaCongelada: TabelaPrecosCompleta;
  breakdown: BreakdownPricing;
}

function getMultiplicador(m: Multiplicadores, perfil: PerfilKey): number {
  return m[perfil] ?? 1;
}

function calcularPacote(
  _bundle: Bundle,
  tabela: TabelaPrecosCompleta,
  pacote: PacoteKey,
  recursos: RecursosPricing,
  multiplicador: number,
): { linhas: LinhaBreakdown[]; subtotal: number } {
  const linhas: LinhaBreakdown[] = [];
  let subtotal = 0;
  const v = tabela.venda;

  const push = (label: string, qtd: number, unitario: number, multAplicado: boolean): void => {
    if (qtd <= 0 || unitario <= 0) return;
    const sub = qtd * unitario;
    linhas.push({ label, qtd, unitario, subtotal: sub, multiplicadorAplicado: multAplicado });
    subtotal += sub;
  };

  if (pacote === 'essencial') {
    push('Estações', recursos.dispositivos, v.essencial?.dispositivo ?? 0, false);
    push('Servidor gerenciado', recursos.servidores, v.essencial?.servidor ?? 0, false);
    push('Usuário Microsoft 365', recursos.usuarios, v.essencial?.usuarioM365 ?? 0, false);
    push('Horas de suporte', recursos.horasSuporte, v.essencial?.horaSuporte ?? 0, false);
  } else if (pacote === 'professional') {
    const precoEstacao = (v.professional?.usuario ?? 0) * multiplicador;
    const precoServidor = (v.professional?.servidorComEdr ?? 0) * multiplicador;
    push('Estações', recursos.dispositivos, precoEstacao, true);
    push('Servidor com EDR', recursos.servidores, precoServidor, true);
    push('Usuário Microsoft 365', recursos.usuarios, v.professional?.usuarioM365 ?? 0, false);
  } else if (pacote === 'premium') {
    const precoEstacao = (v.professional?.usuario ?? 0) * multiplicador;
    const precoServidor = (v.professional?.servidorComEdr ?? 0) * multiplicador;
    push('Estações', recursos.dispositivos, precoEstacao, true);
    push('Servidor com EDR', recursos.servidores, precoServidor, true);
    push('Usuário Microsoft 365', recursos.usuarios, v.professional?.usuarioM365 ?? 0, false);
    push('Equipe de Infraestrutura (3h/mês)', 1, v.premium?.equipeInfra3h ?? 0, false);
    push('Gestor de TI (visita mensal)', 1, v.premium?.gestorMensal ?? 0, false);
  }

  return { linhas, subtotal };
}

interface InventarioSpec {
  key: keyof InventarioPricing;
  label: string;
  preco: (t: TabelaPrecosCompleta) => number;
}

const INVENTARIO_SPECS: InventarioSpec[] = [
  { key: 'impressoras', label: 'Impressoras', preco: (t) => t.venda.dispositivos.impressora },
  { key: 'nobreaks', label: 'Nobreaks', preco: (t) => t.venda.dispositivos.nobreak },
  { key: 'firewalls', label: 'Firewalls', preco: (t) => t.venda.dispositivos.firewall },
  { key: 'roteadoresAp', label: 'Roteadores / AP', preco: (t) => t.venda.dispositivos.roteadorAp },
  { key: 'switches', label: 'Switches', preco: (t) => t.venda.dispositivos.switch },
  { key: 'dvrs', label: 'DVRs', preco: (t) => t.venda.dispositivos.dvr ?? 0 },
  { key: 'storages', label: 'Storages', preco: (t) => t.venda.dispositivos.storage ?? 0 },
  { key: 'outros', label: 'Outros', preco: (t) => t.venda.dispositivos.outros ?? 0 },
];

function calcularInventario(
  tabela: TabelaPrecosCompleta,
  inv: InventarioPricing,
): { linhas: LinhaBreakdown[]; subtotal: number } {
  const linhas: LinhaBreakdown[] = [];
  let subtotal = 0;
  for (const spec of INVENTARIO_SPECS) {
    const qtd = inv[spec.key] ?? 0;
    if (qtd <= 0) continue;
    const unitario = spec.preco(tabela);
    if (unitario <= 0) continue;
    const sub = qtd * unitario;
    linhas.push({
      label: spec.label,
      qtd,
      unitario,
      subtotal: sub,
      multiplicadorAplicado: false,
    });
    subtotal += sub;
  }
  return { linhas, subtotal };
}

interface AddOnSpec {
  key: keyof AddOnsPricing;
  label: string;
  preco: (t: TabelaPrecosCompleta) => number;
}

const ADDON_SPECS: AddOnSpec[] = [
  { key: 'backupM365', label: 'Backup M365 (por usuário)', preco: (t) => t.venda.backupNuvem.m365 },
  {
    key: 'backupEstacao500gb',
    label: 'Backup Estação 500GB',
    preco: (t) => t.venda.backupNuvem.estacao500gb,
  },
  {
    key: 'backupServidorFisico2tb',
    label: 'Backup Servidor Físico 2TB',
    preco: (t) => t.venda.backupNuvem.servidorFisico2tb,
  },
  {
    key: 'backupServidorVirtual2tb',
    label: 'Backup Servidor Virtual 2TB',
    preco: (t) => t.venda.backupNuvem.servidorVirtual2tb,
  },
  {
    key: 'backupPerGb',
    label: 'Backup por GB',
    preco: (t) => t.venda.backupNuvem.perGb,
  },
  {
    key: 'backupTesteRestore',
    label: 'Teste de Restore',
    preco: (t) => t.venda.backupNuvem.testeRestore,
  },
  {
    key: 'draas',
    label: 'DRaaS',
    preco: (t) => t.venda.backupNuvem.draas,
  },
  { key: 'avGerenciado', label: 'AV Gerenciado', preco: (t) => t.venda.seguranca.antivirus },
  { key: 'avEdr', label: 'AV EDR', preco: (t) => t.venda.seguranca.edr },
  { key: 'mikrotik5p', label: 'Mikrotik 5 portas', preco: (t) => t.venda.firewall.mikrotik5p },
  {
    key: 'mikrotikWireless',
    label: 'Mikrotik Wireless',
    preco: (t) => t.venda.firewall.mikrotikWireless,
  },
  {
    key: 'gestaoFirewallCliente',
    label: 'Gestão Firewall Cliente',
    preco: (t) => t.venda.firewall.gestaoFirewallCliente,
  },
];

function calcularAddons(
  tabela: TabelaPrecosCompleta,
  addons: AddOnsPricing,
): { linhas: LinhaBreakdown[]; subtotal: number } {
  const linhas: LinhaBreakdown[] = [];
  let subtotal = 0;
  for (const spec of ADDON_SPECS) {
    const qtd = addons[spec.key] ?? 0;
    if (qtd <= 0) continue;
    const unitario = spec.preco(tabela);
    if (unitario <= 0) continue;
    const sub = qtd * unitario;
    linhas.push({
      label: spec.label,
      qtd,
      unitario,
      subtotal: sub,
      multiplicadorAplicado: false,
    });
    subtotal += sub;
  }
  return { linhas, subtotal };
}

function valorOnboarding(t: TabelaPrecosCompleta, key: OnboardingKey): number {
  if (key === 'none') return 0;
  const map = { express: 'express', standard: 'standard', enterprise: 'enterprise' } as const;
  return t.venda.onboarding[map[key]] ?? 0;
}

/**
 * Resolve o onboarding (qualquer formato de input) para os campos finais
 * do BreakdownPricing.onboarding. Centraliza a lógica de compat.
 */
function resolverOnboarding(
  t: TabelaPrecosCompleta,
  inp: OnboardingInput,
): {
  tipo: OnboardingKey | 'dimensional';
  total: number;
  valorSugerido?: number;
  editadoManual?: boolean;
  breakdown?: {
    baseFixo: number;
    linhas: Array<{ label: string; qtd: number; unitario: number; subtotal: number }>;
    subtotalAntesMultiplicador: number;
    multiplicador: number;
  };
} {
  // Formato legado: string
  if (typeof inp === 'string') {
    return { tipo: inp, total: valorOnboarding(t, inp) };
  }
  // Nenhum cobrado
  if (inp.modo === 'nenhum') {
    return { tipo: 'none', total: 0 };
  }
  // Dimensional (sugerido ou manual)
  return {
    tipo: 'dimensional',
    total: inp.valorFinal,
    valorSugerido: inp.valorSugerido,
    editadoManual: inp.modo === 'manual',
    breakdown: {
      baseFixo: inp.breakdown.baseFixo,
      linhas: inp.breakdown.linhas,
      subtotalAntesMultiplicador: inp.breakdown.subtotalAntesMultiplicador,
      multiplicador: inp.breakdown.multiplicador,
    },
  };
}

/**
 * Helper: extrai duração em meses da string `prazoFidelidade`.
 * '12 meses' → 12, '24 meses' → 24, '36 meses' → 36, 'Sem fidelidade' → 12.
 *
 * "Sem fidelidade" mapeia pra 12 porque o cronograma de desconto e total
 * contrato precisam de uma duração de referência; 12 meses é o padrão.
 */
export function parsePrazoFidelidadeMeses(prazo: string | undefined): number {
  if (!prazo) return 12;
  const m = prazo.match(/(\d+)/);
  if (!m) return 12;
  const n = parseInt(m[1] ?? '12', 10);
  return Number.isFinite(n) && n > 0 ? n : 12;
}

/**
 * Helper de display: traduz qualquer formato de onboarding (legado ou
 * dimensional) para um rótulo legível na UI admin.
 */
export function rotuloOnboarding(o: OnboardingInput): string {
  if (typeof o === 'string') {
    if (o === 'none') return 'Sem onboarding';
    if (o === 'express') return 'Express (legado)';
    if (o === 'standard') return 'Standard (legado)';
    if (o === 'enterprise') return 'Enterprise (legado)';
    return 'Onboarding';
  }
  if (o.modo === 'nenhum') return 'Sem onboarding';
  if (o.modo === 'manual') return 'Onboarding (editado)';
  return 'Onboarding';
}

/**
 * Helper: encapsula `calcularPricing` e devolve um PricingSnapshot pronto
 * pra gravar na proposta (com versão da tabela, timestamp e tabelaCongelada).
 * Use no backend ao criar/atualizar proposta.
 */
export function gerarPricingSnapshot(inp: InputCalculoPricing): PricingSnapshot {
  const breakdown = calcularPricing(inp);
  return {
    versaoTabela: inp.tabela.versao,
    geradoEm: new Date().toISOString(),
    tabelaCongelada: inp.tabela,
    breakdown,
  };
}

// ─── Onboarding dimensional (modelo novo, 2026-05+) ──────────────────────

/**
 * Linha do breakdown do onboarding dimensional.
 * Cada componente que somou ao total vira uma linha pra exibição.
 */
export interface OnboardingLinhaBreakdown {
  label: string;
  qtd: number;
  unitario: number;
  subtotal: number;
}

/**
 * Resultado completo do cálculo dimensional de onboarding.
 * Estrutura imutável — gravada no payload pra reconstituição futura.
 */
export interface OnboardingSugerido {
  baseFixo: number;
  linhas: OnboardingLinhaBreakdown[];
  /** Subtotal antes do multiplicador (base + somas dos componentes) */
  subtotalAntesMultiplicador: number;
  /** Perfil de ambiente aplicado */
  perfil: PerfilKey;
  multiplicador: number;
  /** Valor sugerido final = subtotal × multiplicador */
  total: number;
}

/**
 * Inputs para o cálculo dimensional do onboarding.
 * Quantidades vêm do payload da proposta (recursos + inventário).
 */
export interface InputsOnboardingDimensional {
  estacoes: number;
  servidores: number;
  /** Firewalls do inventário do cliente */
  firewalls: number;
  /** Dispositivos de rede extra: switches gerenciados + APs + NAS */
  dispositivosRede: number;
  perfil: PerfilKey;
}

/**
 * Calcula o onboarding sugerido pelo modelo dimensional.
 *
 * Fórmula: (baseFixo + Σ componentes) × multiplicador de ambiente
 *
 * Componentes que ficam em 0 são omitidos do breakdown pra UI ficar limpa.
 */
export function calcularOnboardingSugerido(
  tabela: TabelaPrecosCompleta,
  inp: InputsOnboardingDimensional,
): OnboardingSugerido {
  const o = tabela.venda.onboarding;
  const baseFixo = o.baseFixo ?? 0;
  const linhas: OnboardingLinhaBreakdown[] = [];

  const push = (label: string, qtd: number, unitario: number): void => {
    if (qtd <= 0 || unitario <= 0) return;
    linhas.push({ label, qtd, unitario, subtotal: qtd * unitario });
  };

  push('Estações de trabalho', inp.estacoes, o.valorPorEstacao ?? 0);
  push('Servidores', inp.servidores, o.valorPorServidor ?? 0);
  push('Firewall do cliente', inp.firewalls, o.valorPorFirewall ?? 0);
  push('Dispositivos de rede extra', inp.dispositivosRede, o.valorPorDispositivoRede ?? 0);

  const somaComponentes = linhas.reduce((s, l) => s + l.subtotal, 0);
  const subtotalAntesMultiplicador = baseFixo + somaComponentes;
  const multiplicador = getMultiplicador(tabela.multiplicadores, inp.perfil);
  // Garante que multiplicador nunca seja < 1.00 (regra do briefing)
  const multiplicadorEfetivo = Math.max(1, multiplicador);
  const total = Math.round(subtotalAntesMultiplicador * multiplicadorEfetivo * 100) / 100;

  return {
    baseFixo,
    linhas,
    subtotalAntesMultiplicador,
    perfil: inp.perfil,
    multiplicador: multiplicadorEfetivo,
    total,
  };
}

/**
 * Função principal: recebe inputs, devolve breakdown completo.
 */
export function calcularPricing(inp: InputCalculoPricing): BreakdownPricing {
  // Proposta avulsa (à la carte): zera pacote base, inventário e Premium.
  const semBase = inp.semPacoteBase === true;
  const bundle = inp.tabela.bundles[inp.pacote];
  const multiplicador = getMultiplicador(inp.tabela.multiplicadores, inp.perfil);
  const pacote = semBase
    ? { linhas: [], subtotal: 0 }
    : calcularPacote(bundle, inp.tabela, inp.pacote, inp.recursos, multiplicador);
  // Regra de negócio: pacote Essencial NÃO cobra dispositivos do parque —
  // a quantidade aparece no resumo, mas não soma no investimento mensal.
  // Avulso também não tem inventário de parque.
  const inventarioEfetivo =
    semBase || inp.pacote === 'essencial' ? INVENTARIO_PRICING_VAZIO : inp.inventario;
  const inventario = calcularInventario(inp.tabela, inventarioEfetivo);
  const addons = calcularAddons(inp.tabela, inp.addons);
  // HaaS — calcula subtotal e soma ao mensal recorrente (entra no contrato).
  const haasBreak = calcularSubtotalHaas(inp.haasItens);
  // Defesa em profundidade: módulos Premium só somam se pacote=premium E não for avulso.
  // Se proposta era Premium e mudou pra Professional/Essencial, payload.premiumModulos
  // pode persistir do snapshot anterior — ignora aqui.
  const premiumBreak = calcularSubtotalPremiumModulos(
    !semBase && inp.pacote === 'premium' ? inp.premiumModulos : undefined,
    inp.tabela,
  );
  const mensalRecorrente =
    pacote.subtotal +
    inventario.subtotal +
    addons.subtotal +
    haasBreak.totalMensal +
    premiumBreak.totalLiquido;

  const onboardingResolvido = resolverOnboarding(inp.tabela, inp.onboarding);
  const onboardingTotal = onboardingResolvido.total;
  const onboardingParcela = onboardingTotal > 0 ? onboardingTotal / 6 : 0;

  // Desconto escalonado — opcional. Quando presente, recalcula totalContrato
  // somando faixa por faixa, em vez do mensalRecorrente × 12 simples.
  const duracaoContratoMeses = inp.prazoFidelidadeMeses ?? 12;
  const descontoBreakdown =
    inp.descontoEscalonado && inp.descontoEscalonado.faixas.length > 0
      ? calcularDescontoEscalonado(
          mensalRecorrente,
          onboardingParcela,
          duracaoContratoMeses,
          inp.descontoEscalonado,
        )
      : undefined;

  // Total contrato: o cronograma já inclui onboarding mês-a-mês (parcela 1-6).
  // Quando NÃO há desconto, soma reta: mensal × meses + onboarding total.
  const totalContrato = descontoBreakdown
    ? descontoBreakdown.totalContratoComDesconto
    : mensalRecorrente * duracaoContratoMeses + onboardingTotal;

  return {
    pacote: {
      nome: semBase ? 'Sem pacote (avulso)' : bundle.nome,
      linhas: pacote.linhas,
      subtotal: pacote.subtotal,
    },
    inventario: { linhas: inventario.linhas, subtotal: inventario.subtotal },
    addons: { linhas: addons.linhas, subtotal: addons.subtotal },
    onboarding: {
      tipo: onboardingResolvido.tipo,
      total: onboardingTotal,
      parcela: onboardingParcela,
      ...(onboardingResolvido.valorSugerido !== undefined
        ? { valorSugerido: onboardingResolvido.valorSugerido }
        : {}),
      ...(onboardingResolvido.editadoManual !== undefined
        ? { editadoManual: onboardingResolvido.editadoManual }
        : {}),
      ...(onboardingResolvido.breakdown ? { breakdown: onboardingResolvido.breakdown } : {}),
    },
    multiplicador,
    mensalRecorrente,
    mensalPrimeiros6Meses: mensalRecorrente + onboardingParcela,
    totalContrato12m: totalContrato,
    perfil: inp.perfil,
    pacote_key: inp.pacote,
    ...(semBase ? { semPacoteBase: true } : {}),
    // HaaS só aparece no breakdown quando há itens — UI usa pra mostrar bloco separado.
    ...(haasBreak.linhas.length > 0 ? { haas: haasBreak } : {}),
    ...(premiumBreak.linhas.length > 0 ? { premiumModulos: premiumBreak } : {}),
    ...(descontoBreakdown ? { descontoEscalonado: descontoBreakdown } : {}),
  };
}

/**
 * Parseia o markdown gerado por `montarObservacoesEstruturadas` (form de
 * nova proposta) e extrai os blocos de Inventário do parque e Add-ons.
 */
export function parsearInventarioEAddonsDoMarkdown(observacoes: string): {
  inventario: InventarioPricing;
  addons: AddOnsPricing;
} {
  const inventario: InventarioPricing = { ...INVENTARIO_PRICING_VAZIO };
  const addons: AddOnsPricing = { ...ADDONS_PRICING_VAZIO };
  if (!observacoes) return { inventario, addons };

  const partes = observacoes.split(/\n## /).map((s, i) => (i === 0 ? s.replace(/^## /, '') : s));
  const mapa: Record<string, string> = {};
  for (const sec of partes) {
    const [header, ...body] = sec.split('\n');
    if (!header) continue;
    mapa[header.trim()] = body.join('\n').trim();
  }

  // ── Bloco "## Inventário do parque" → preenche InventarioPricing ────────
  // Labels (vide montarObservacoesEstruturadas): Impressoras, Nobreaks, Firewall,
  // Roteador/AP, Switch, DVR, Storage, Outros
  const invMd = mapa['Inventário do parque'] ?? mapa['Inventario do parque'];
  if (invMd) {
    for (const linha of invMd.split('\n')) {
      const m = /^-\s*(.+?):\s*(\d+)/.exec(linha.trim());
      if (!m || !m[1] || !m[2]) continue;
      const label = m[1].toLowerCase();
      const qtd = Number(m[2]);
      if (label.includes('impressora')) {
        inventario.impressoras = qtd;
      } else if (label.includes('nobreak')) {
        inventario.nobreaks = qtd;
      } else if (label.includes('firewall')) {
        inventario.firewalls = qtd;
      } else if (label.includes('roteador') || label.includes('ap')) {
        inventario.roteadoresAp = qtd;
      } else if (label.includes('switch')) {
        inventario.switches = qtd;
      } else if (label.includes('dvr') || label.includes('nvr')) {
        inventario.dvrs = qtd;
      } else if (label.includes('storage')) {
        inventario.storages = qtd;
      } else if (label.includes('outro')) {
        inventario.outros = qtd;
      }
    }
  }

  // ── Bloco "## Add-ons mensais" → preenche AddOnsPricing ────────────────
  const addsMd = mapa['Add-ons mensais'];
  if (addsMd) {
    for (const linha of addsMd.split('\n')) {
      const m = /^-\s*(.+?):\s*(\d+)/.exec(linha.trim());
      if (!m || !m[1] || !m[2]) continue;
      const label = m[1].toLowerCase();
      const qtd = Number(m[2]);
      if (label.includes('backup m365')) {
        addons.backupM365 = qtd;
      } else if (label.includes('backup estacao') || label.includes('backup estação')) {
        addons.backupEstacao500gb = qtd;
      } else if (
        label.includes('backup servidor fisico') ||
        label.includes('backup servidor físico')
      ) {
        addons.backupServidorFisico2tb = qtd;
      } else if (label.includes('backup servidor virtual')) {
        addons.backupServidorVirtual2tb = qtd;
      } else if (label.includes('backup por gb') || label === 'backup gb') {
        addons.backupPerGb = qtd;
      } else if (label.includes('teste de restore') || label.includes('teste restore')) {
        addons.backupTesteRestore = qtd;
      } else if (label.includes('draas')) {
        addons.draas = qtd;
      } else if (label.includes('av gerenciado')) {
        addons.avGerenciado = qtd;
      } else if (label.includes('av edr')) {
        addons.avEdr = qtd;
      } else if (label.includes('mikrotik 5p')) {
        addons.mikrotik5p = qtd;
      } else if (label.includes('mikrotik wireless')) {
        addons.mikrotikWireless = qtd;
      } else if (label.includes('gestao firewall') || label.includes('gestão firewall')) {
        addons.gestaoFirewallCliente = qtd;
      }
    }
  }

  return { inventario, addons };
}

// ──────────────────────────────────────────────────────────────────────
// Pricing renovação (#243) — direto dos itens livres, sem tabela
// ──────────────────────────────────────────────────────────────────────

/** Item da renovação com reajuste aplicado (snapshot pra view/PDF). */
export interface ItemRenovacaoComputado {
  descricao: string;
  quantidade: number;
  valorUnitario: number;
  valorTotal: number;
  /** Valor unit. depois do reajuste linear. */
  valorUnitarioReajustado: number;
  /** Valor total depois do reajuste (quantidade × valorUnitarioReajustado). */
  valorTotalReajustado: number;
  codigoServicoOmie?: string;
}

/**
 * Pricing snapshot dedicado à renovação (#243). NÃO usa TabelaPrecosCompleta.
 * Cálculo direto: sum(itens × reajuste) com desconto opcional.
 */
export interface PricingRenovacao {
  geradoEm: string;
  /** Soma dos valorTotal dos itens, ANTES do reajuste. */
  subtotalAntesReajuste: number;
  /** Percentual aplicado (0 se sem-reajuste). */
  percentualReajuste: number;
  /** Soma dos valorTotalReajustado dos itens. */
  subtotalComReajuste: number;
  itensComputados: ItemRenovacaoComputado[];
  /** Breakdown do desconto escalonado (opcional). Reusa tipo do contrato-recorrente. */
  descontoBreakdown?: DescontoEscalonadoBreakdown;
  /** Mensal recorrente final (mês 1 após reajuste e desconto se aplicável). */
  mensalRecorrente: number;
  /** Prazo de fidelidade (12/24/36 meses). */
  prazoFidelidadeMeses: number;
  /** Soma do contrato inteiro (considerando faixas de desconto). */
  totalContrato: number;
}

export interface InputCalculoPricingRenovacao {
  itens: Array<{
    descricao: string;
    quantidade: number;
    valorUnitario: number;
    valorTotal: number;
    codigoServicoOmie?: string;
  }>;
  /** 0 se 'sem-reajuste'. */
  reajustePercentual: number;
  /** Desconto opcional. Reusa schema do contrato-recorrente. */
  descontoEscalonado?: DescontoEscalonado;
  /** Prazo de fidelidade (12/24/36 meses). */
  prazoFidelidadeMeses: number;
}

const _round2Renovacao = (n: number): number => Math.round(n * 100) / 100;

export function calcularPricingRenovacao(inp: InputCalculoPricingRenovacao): PricingRenovacao {
  const fator = 1 + (inp.reajustePercentual ?? 0) / 100;

  const itensComputados: ItemRenovacaoComputado[] = inp.itens.map((it) => {
    const valorUnitarioReajustado = _round2Renovacao(it.valorUnitario * fator);
    const valorTotalReajustado = _round2Renovacao(it.quantidade * valorUnitarioReajustado);
    return {
      descricao: it.descricao,
      quantidade: it.quantidade,
      valorUnitario: it.valorUnitario,
      valorTotal: it.valorTotal,
      valorUnitarioReajustado,
      valorTotalReajustado,
      codigoServicoOmie: it.codigoServicoOmie,
    };
  });

  const subtotalAntesReajuste = _round2Renovacao(
    itensComputados.reduce((s, it) => s + it.valorTotal, 0),
  );
  const subtotalComReajuste = _round2Renovacao(
    itensComputados.reduce((s, it) => s + it.valorTotalReajustado, 0),
  );

  // Aplica desconto escalonado mês-a-mês se houver
  let descontoBreakdown: DescontoEscalonadoBreakdown | undefined;
  let mensalMes1 = subtotalComReajuste;
  let totalContrato = subtotalComReajuste * inp.prazoFidelidadeMeses;

  if (inp.descontoEscalonado && inp.descontoEscalonado.faixas.length > 0) {
    const r = _calcularDescontoRenovacao(
      subtotalComReajuste,
      inp.descontoEscalonado,
      inp.prazoFidelidadeMeses,
    );
    descontoBreakdown = r.breakdown;
    mensalMes1 = r.mensalMes1;
    totalContrato = r.totalContrato;
  }

  return {
    geradoEm: new Date().toISOString(),
    subtotalAntesReajuste,
    percentualReajuste: inp.reajustePercentual ?? 0,
    subtotalComReajuste,
    itensComputados,
    descontoBreakdown,
    mensalRecorrente: _round2Renovacao(mensalMes1),
    prazoFidelidadeMeses: inp.prazoFidelidadeMeses,
    totalContrato: _round2Renovacao(totalContrato),
  };
}

/**
 * Aplica desconto escalonado mês-a-mês sobre o subtotal mensal da renovação.
 * Mais simples que `calcularDescontoEscalonado` do contrato-recorrente
 * porque renovação NÃO tem onboarding parcelado.
 */
function _calcularDescontoRenovacao(
  mensalBase: number,
  desc: DescontoEscalonado,
  prazoMeses: number,
): { breakdown: DescontoEscalonadoBreakdown; mensalMes1: number; totalContrato: number } {
  const cronograma: CronogramaLinha[] = [];
  let totalContratoComDesconto = 0;
  let mes = 1;

  for (const faixa of desc.faixas) {
    let valorMensalComDesc = mensalBase;
    if (faixa.tipo === 'pct') {
      const descontoMes = _round2Renovacao((mensalBase * faixa.valor) / 100);
      valorMensalComDesc = _round2Renovacao(Math.max(0, mensalBase - descontoMes));
    } else {
      valorMensalComDesc = _round2Renovacao(Math.max(0, mensalBase - faixa.valor));
    }
    const dur = Math.min(faixa.duracaoMeses, prazoMeses - mes + 1);
    if (dur <= 0) break;

    cronograma.push({
      mesInicio: mes,
      mesFim: mes + dur - 1,
      duracaoMeses: dur,
      valorMensal: valorMensalComDesc,
      tipo: faixa.tipo,
      valorAplicado: faixa.valor,
      motivo: faixa.motivo,
    });
    totalContratoComDesconto += valorMensalComDesc * dur;
    mes += dur;
  }

  // Depois das faixas, volta ao valor cheio até o fim do prazo
  if (mes <= prazoMeses) {
    const dur = prazoMeses - mes + 1;
    cronograma.push({
      mesInicio: mes,
      mesFim: prazoMeses,
      duracaoMeses: dur,
      valorMensal: mensalBase,
      tipo: null,
      valorAplicado: null,
    });
    totalContratoComDesconto += mensalBase * dur;
  }

  const totalContratoSemDesconto = _round2Renovacao(mensalBase * prazoMeses);
  const breakdown: DescontoEscalonadoBreakdown = {
    cronograma,
    mensalCheio: mensalBase,
    totalContratoComDesconto: _round2Renovacao(totalContratoComDesconto),
    totalContratoSemDesconto,
    economiaTotal: _round2Renovacao(totalContratoSemDesconto - totalContratoComDesconto),
    config: {
      aplicarEm: 'mensal',
      duracaoContratoMeses: prazoMeses,
    },
  };

  return {
    breakdown,
    mensalMes1: cronograma[0]?.valorMensal ?? mensalBase,
    totalContrato: totalContratoComDesconto,
  };
}
